import { useState, useEffect, useMemo, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Label,
  Input,
  Badge,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { ArrowLeft, Save, Send, FileText, Plus, Trash2 } from 'react-feather';
import ComponentCard from '../../layouts/containers/ComponentCard';
import ModalClickable from '../../layouts/modals/ModalClickable';
import MyEditor from '../../layouts/myEditor/MyEditor';
import presupuestosApiService from '../../services/http/presupuestosApiService';
import logger from '../../services/logger';
import colors from '../../theme/colors';
import { AuthContext } from '../../context/authContext';
import { getItem, STORAGE_KEYS } from '../../services/storage';
import { isSintesisOFCT, withDefaultTramo, inicioPorTramo } from '../../helpers/actividades';

const ESTADOS = [
  { value: 0, label: 'Gestionándose - Gestionándose' },
  { value: 1, label: 'Revisión Tit. - Gestionándose' },
  { value: 2, label: 'Pendiente Alum. - Interesado/informado' },
  { value: 3, label: 'Tramitación Secr. - Enviado a matricular' },
  { value: 4, label: 'Matriculado - Matriculado' },
  { value: 5, label: 'Rechazado - Desestimado' },
  { value: 6, label: 'Aceptado - Interesado/informado' },
];

const estadoColor = { 0: 'warning', 1: 'info', 2: 'primary', 3: 'secondary', 4: 'success', 5: 'danger', 6: 'success' };

// Solo admins, Alba (asanjose) y Natalia (nfeneux) pueden elegir si mostrar precios en el PDF
const PRECIOS_PDF_ALLOWED_EMAILS = [
  'asanjose@cedetecgroup.com',
  'nfeneux@cedetecgroup.com',
];

const DetallePresupuesto = () => {
  const { headquarter, id } = useParams();
  const navigate = useNavigate();
  const { userOU } = useContext(AuthContext);
  const userEmail = getItem(STORAGE_KEYS.USER)?.profileObj?.email;
  const isAdmin = userOU?.idCedesca === 0 && userOU?.idCntec === 0;
  const esSecretaria = userOU?.idCedesca === -3 && userOU?.idCntec === -3;
  // El PDF interno (secretaría) lo pueden ver admins y OU secretaría.
  const puedeVerPdfInterno = isAdmin || esSecretaria;
  const puedeElegirPrecios = isAdmin || PRECIOS_PDF_ALLOWED_EMAILS.includes(userEmail);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [estado, setEstado] = useState(0);
  const [comentInt, setComentInt] = useState('');
  const [comentExt, setComentExt] = useState('');
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [dni, setDni] = useState('');
  const [correo, setCorreo] = useState('');
  // Precio de inscripción editable: permite a secretaría/admin corregirlo a mano
  // de forma puntual (ex-alumno, hermano, precio mal en catálogo...) sin bloquear
  // la matrícula. El backend lo persiste tal cual (tolera 0 = descuento total).
  const [precioInscr, setPrecioInscr] = useState('');
  const [actividades, setActividades] = useState([]);
  const [disponibles, setDisponibles] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [planesOriginales, setPlanesOriginales] = useState([]);
  // Arrastre de actividades anuales (sin T1/T2) hacia un bloque concreto
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverInicio, setDragOverInicio] = useState(null);
  // 2º año interno: módulos del MISMO grupo del presupuesto (cabecera) que aún
  // no están en la propuesta. No suman al precio ni se envían al alumno; se
  // envían con el flag `interno` para que el backend los trate como tales aun
  // compartiendo grupo con la cabecera.
  const [segundoAnio, setSegundoAnio] = useState([]);
  const [modulosGrupoCabecera, setModulosGrupoCabecera] = useState([]);
  const [loadingModulosSegundo, setLoadingModulosSegundo] = useState(false);

  const TIPO_OPTIONS = [
    { value: '1', label: 'U' },
    { value: '2', label: 'Y' },
    { value: '3', label: 'A' },
  ];
  const todayISO = new Date().toISOString().split('T')[0];

  const isoFromBackend = (val) => {
    if (!val) return null;
    try {
      return new Date(val).toISOString().slice(0, 10);
    } catch {
      return null;
    }
  };

  const normalizarPlan = (row) => ({
    Id: row.Id,
    presu_id: row.presu_id,
    Cuotas: parseInt(row.Cuotas, 10) || 1,
    Fecha_ini: isoFromBackend(row.Fecha_ini) || '',
    Fecha_fin: isoFromBackend(row.Fecha_fin),
    tipo: row.tipo ? String(row.tipo) : '',
  });

  const cargarPlanes = async () => {
    try {
      const list = await presupuestosApiService.listarPlanesPago({
        headquarter,
        presu_id: parseInt(id, 10),
      });
      const normalizados = Array.isArray(list) ? list.map(normalizarPlan) : [];
      setPlanes(normalizados);
      setPlanesOriginales(normalizados);
    } catch (error) {
      logger.error('Error cargando planes de pago:', error);
      setPlanes([]);
      setPlanesOriginales([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await presupuestosApiService.obtenerPresupuesto({ headquarter, id });
        setData(result);
        setEstado(result.EstadoId ?? 0);
        setComentInt(result.coment_int || '');
        setComentExt(result.coment_ext || '');
        setNombre(result.Nombre || '');
        setApellidos(result.Apellidos || '');
        setDni(result.DNI || '');
        setCorreo(result.Correo || '');
        setPrecioInscr(
          result.precio_inscr != null && result.precio_inscr !== ''
            ? String(result.precio_inscr)
            : '',
        );
        // Separar 1er año de 2º año. El backend marca las líneas internas de 2º
        // año con interno:1 (comparten el grupo de cabecera). Se mantiene el
        // respaldo por grupo distinto para datos antiguos guardados antes del flag.
        const cab = String(result.idgrupo ?? '').trim();
        const grupoLinea = (a) => String(a.CodigoGrupo ?? a.IdGrupo ?? a.idgrupo ?? '').trim();
        const esSegundo = (a) => {
          if (Number(a.interno) === 1) return true;
          const g = grupoLinea(a);
          return Boolean(g && cab && g !== cab);
        };
        const todas = (result.actividades || []).map((a) => ({ ...a, seleccionada: true }));
        setActividades(withDefaultTramo(todas.filter((a) => !esSegundo(a))));
        setSegundoAnio(
          todas.filter(esSegundo).map((a) => ({ ...a, idgrupo: grupoLinea(a) || cab })),
        );
        setDisponibles(
          withDefaultTramo(
            (result.actividadesNo || []).map((a) => ({ ...a, seleccionada: false })),
          ),
        );
        await cargarPlanes();
      } catch (error) {
        logger.error('Error cargando presupuesto:', error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headquarter, id]);

  const precioActividades = useMemo(
    () => actividades.reduce((sum, a) => sum + (parseFloat(a.precio) || 0), 0),
    [actividades],
  );

  const actKey = (a) => `${a.codigo || ''}|${a.descripcion || ''}|${a.modulo || ''}`;

  const addActividad = (act) => {
    setActividades((prev) => [...prev, { ...act, seleccionada: true }]);
    setDisponibles((prev) => prev.filter((d) => actKey(d) !== actKey(act)));
  };

  const removeActividadByItem = (item) => {
    const key = actKey(item);
    setActividades((prev) => prev.filter((a) => actKey(a) !== key));
    setDisponibles((prev) => {
      if (prev.some((d) => actKey(d) === key)) return prev;
      return [...prev, { ...item, seleccionada: false }];
    });
  };

  // Las T1/T2 (y Síntesis/FCT, que siempre llevan tramo) se autoasignan con clic.
  // Las anuales (sin tramo T1/T2) no se autoasignan: el usuario las arrastra al bloque.
  const isAnual = (item) => {
    const t = (item.tramo || '').toUpperCase();
    return t !== 'T1' && t !== 'T2';
  };

  // Asigna (o reubica) una actividad a un bloque concreto fijando su Inicio.
  const asignarAInicio = (item, inicio) => {
    const key = actKey(item);
    setActividades((prev) => {
      if (prev.some((a) => actKey(a) === key)) {
        return prev.map((a) => (actKey(a) === key ? { ...a, Inicio: inicio } : a));
      }
      return [...prev, { ...item, seleccionada: true, Inicio: inicio }];
    });
    setDisponibles((prev) => prev.filter((d) => actKey(d) !== key));
  };

  const handleDragStart = (item) => (e) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', actKey(item));
    } catch {
      /* algunos navegadores requieren setData; si falla, seguimos con el estado */
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverInicio(null);
  };

  const handleDropInicio = (inicio) => (e) => {
    e.preventDefault();
    setDragOverInicio(null);
    if (draggedItem) asignarAInicio(draggedItem, inicio);
    setDraggedItem(null);
  };

  // --- 2º año interno ---
  const grupoCabecera = String(data?.idgrupo ?? '').trim();

  // Ya no se elige otro grupo: se cargan automáticamente los módulos del grupo
  // del propio presupuesto (cabecera) y se ofrecen los que aún no están en la
  // propuesta. Se dispara al conocer el grupo de cabecera.
  useEffect(() => {
    if (!grupoCabecera) return undefined;
    let cancelado = false;
    const cargarModulosCabecera = async () => {
      setLoadingModulosSegundo(true);
      try {
        const rows = await presupuestosApiService.buscarModulosGrupo({
          headquarter,
          groupId: grupoCabecera,
        });
        const modulos = (Array.isArray(rows) ? rows : []).map((r) => ({
          codigo: r.codigo,
          codigocurso: r.codigo,
          descripcion: r.descripcion,
          modulo: r.modulo,
          nombreModulo: r.nombre,
          idPlan: r.IdPlan,
          idaño: r.IdAño,
          idgrupo: grupoCabecera,
          tramo: r.tramo,
          Inicio: r.Inicio,
          horas: r.horas,
          curso: r.curso,
        }));
        if (!cancelado) setModulosGrupoCabecera(modulos);
      } catch (error) {
        logger.error('Error cargando módulos del grupo del presupuesto:', error);
        if (!cancelado) setModulosGrupoCabecera([]);
      } finally {
        if (!cancelado) setLoadingModulosSegundo(false);
      }
    };
    cargarModulosCabecera();
    return () => {
      cancelado = true;
    };
  }, [headquarter, grupoCabecera]);

  // Módulos del grupo de cabecera que aún no están en la propuesta (ni en 1er
  // año ni ya añadidos al 2º año interno).
  const disponiblesSegundo = useMemo(() => {
    const keyOf = (a) => `${a.codigo || ''}|${a.descripcion || ''}|${a.modulo || ''}`;
    const seleccionados = new Set([
      ...actividades.map(keyOf),
      ...segundoAnio.map(keyOf),
    ]);
    return modulosGrupoCabecera.filter((m) => !seleccionados.has(keyOf(m)));
  }, [modulosGrupoCabecera, actividades, segundoAnio]);

  const addSegundoAnio = (item) => {
    setSegundoAnio((prev) => [...prev, { ...item, seleccionada: true }]);
  };

  const removeSegundoAnio = (item) => {
    const key = actKey(item);
    setSegundoAnio((prev) => prev.filter((a) => actKey(a) !== key));
  };

  // Permitir decimales: solo dígitos y un separador decimal (',' -> '.'), máx 2 decimales.
  const limpiarPrecio = (raw) => {
    let cleaned = String(raw).replace(',', '.').replace(/[^\d.]/g, '');
    const dot = cleaned.indexOf('.');
    if (dot !== -1) {
      const entero = cleaned.slice(0, dot);
      const decimales = cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2);
      cleaned = `${entero}.${decimales}`;
    }
    return cleaned;
  };

  const handlePrecioActividadChange = (item, raw) => {
    const cleaned = limpiarPrecio(raw);
    const key = actKey(item);
    setActividades((prev) =>
      prev.map((a) => (actKey(a) === key ? { ...a, precio: cleaned } : a)),
    );
  };

  // Precio del módulo de 2º año (interno). Editable por secretaría para que
  // prevalezca sobre el precio por defecto del curso (curso_precio_sede).
  const handlePrecioSegundoChange = (item, raw) => {
    const cleaned = limpiarPrecio(raw);
    const key = actKey(item);
    setSegundoAnio((prev) =>
      prev.map((a) => (actKey(a) === key ? { ...a, precio: cleaned } : a)),
    );
  };

  const handleTramoChange = (item, nuevoTramo) => {
    const key = actKey(item);
    const inicio = inicioPorTramo(nuevoTramo);
    const updater = (list) =>
      list.map((a) => (actKey(a) === key ? { ...a, tramo: nuevoTramo, Inicio: inicio } : a));
    setActividades(updater);
    setDisponibles(updater);
  };

  const getInicio = (item) => {
    // El tramo manda para módulos con T1/T2 (lo que muestra y edita el selector):
    // T2 -> febrero, T1 -> septiembre. Así un Inicio heredado del backend (una
    // fecha, el inicio del curso, etc.) no descoloca una Síntesis/FCT en T2.
    const t = (item.tramo || '').toUpperCase();
    if (t === 'T1' || t === 'T2') return inicioPorTramo(t);
    // Anuales (sin tramo): se ubican por su Inicio; febrero solo si lo indica.
    return (item.Inicio || '').toLowerCase().includes('feb') ? 'febrero' : 'septiembre';
  };
  const septiembreItems = actividades.filter((a) => getInicio(a) === 'septiembre');
  const febreroItems = actividades.filter((a) => getInicio(a) === 'febrero');

  const tramoBadgeColor = (tramo) => {
    const t = (tramo || '').toUpperCase();
    if (t === 'T1') return '#2563eb';
    if (t === 'T2') return '#7c3aed';
    return '#059669';
  };
  const cursoLabel = (curso) => {
    const c = (curso || '').toUpperCase();
    if (c === 'PRIMERO') return '1er año';
    if (c === 'SEGUNDO') return '2º año';
    return curso || '';
  };
  const cursoColor = (curso) => {
    const c = (curso || '').toUpperCase();
    if (c === 'PRIMERO') return '#0891b2';
    if (c === 'SEGUNDO') return '#ea580c';
    return '#6b7280';
  };
  const chipStyle = (color) => ({
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '4px',
    backgroundColor: `${color}20`,
    color,
    whiteSpace: 'nowrap',
  });
  const sumPrecio = (items) =>
    items.reduce((sum, it) => sum + (parseFloat(it.precio) || 0), 0);
  const sumHoras = (items) =>
    items.reduce((sum, it) => sum + (parseInt(it.horas, 10) || 0), 0);

  const renderModuloCard = (item, esDisponible) => {
    const anual = isAnual(item);
    // En disponibles, las anuales solo se asignan arrastrando (no por clic).
    const clicSinEfecto = esDisponible && anual;
    const handleCardClick = () => {
      if (clicSinEfecto) return;
      if (esDisponible) addActividad(item);
      else removeActividadByItem(item);
    };
    return (
    <div
      key={`${item.modulo}-${item.codigo}-${item.descripcion}`}
      role="button"
      tabIndex={0}
      draggable={anual}
      onDragStart={anual ? handleDragStart(item) : undefined}
      onDragEnd={anual ? handleDragEnd : undefined}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      title={clicSinEfecto ? 'Arrastra esta actividad anual al bloque que quieras' : undefined}
      style={{
        padding: '8px 10px',
        marginBottom: '6px',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
        backgroundColor: '#fff',
        cursor: anual ? 'grab' : 'pointer',
        fontSize: '0.8rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', justifyContent: 'space-between' }}>
        <strong style={{ flex: 1 }}>
          {(item.nombreModulo || item.modulo || '').split(' - ')[0].trim()}
        </strong>
        {isSintesisOFCT(item) ? (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <Input
              type="select"
              bsSize="sm"
              value={(item.tramo || 'T1').toUpperCase()}
              onChange={(e) => handleTramoChange(item, e.target.value)}
              style={{
                width: '110px',
                fontSize: '0.7rem',
                padding: '2px 4px',
                height: 'auto',
              }}
              aria-label="Tramo"
            >
              <option value="T1">T1 · Septiembre</option>
              <option value="T2">T2 · Febrero</option>
            </Input>
          </div>
        ) : item.tramo ? (
          <span style={chipStyle(tramoBadgeColor(item.tramo))}>{item.tramo}</span>
        ) : (
          clicSinEfecto && (
            <span style={chipStyle('#059669')} title="Arrástrala al bloque que quieras">
              Anual · arrastrar
            </span>
          )
        )}
      </div>
      {item.descripcion && (
        <div style={{ color: '#6b7280', fontSize: '0.72rem' }}>{item.descripcion}</div>
      )}
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
        {item.curso && <span style={chipStyle(cursoColor(item.curso))}>{cursoLabel(item.curso)}</span>}
        {item.horas != null && item.horas !== '' && (
          <span style={chipStyle('#475569')}>{item.horas}h</span>
        )}
        {esDisponible
          ? item.precio != null &&
            item.precio !== '' && (
              <span style={chipStyle('#059669')}>{(parseFloat(item.precio) || 0).toFixed(2)} €</span>
            )
          : (
            <div
              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="presentation"
            >
              <Input
                type="text"
                inputMode="decimal"
                bsSize="sm"
                style={{ width: '90px', textAlign: 'right', fontSize: '0.72rem', padding: '2px 4px' }}
                value={item.precio ?? ''}
                onChange={(e) => handlePrecioActividadChange(item, e.target.value)}
              />
              <span style={{ fontSize: '0.72rem', color: '#059669', fontWeight: 600 }}>€</span>
            </div>
          )}
      </div>
    </div>
    );
  };

  const renderColumna = (titulo, items, vacioMsg, mostrarTotal, inicioDrop = null) => {
    const esDisponible = titulo === 'Módulos disponibles';
    const isDropTarget = inicioDrop != null;
    const dragActivo = draggedItem != null;
    const resaltado = isDropTarget && dragActivo && dragOverInicio === inicioDrop;
    return (
      <Col md="4">
        <div
          onDragOver={
            isDropTarget
              ? (e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragOverInicio !== inicioDrop) setDragOverInicio(inicioDrop);
                }
              : undefined
          }
          onDragLeave={
            isDropTarget
              ? () => setDragOverInicio((cur) => (cur === inicioDrop ? null : cur))
              : undefined
          }
          onDrop={isDropTarget ? handleDropInicio(inicioDrop) : undefined}
          style={{
            backgroundColor: resaltado ? '#eff6ff' : '#f9fafb',
            border: resaltado ? '2px dashed #2563eb' : '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px',
            minHeight: '300px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <h6 style={{ marginBottom: '12px', fontSize: '0.85rem', fontWeight: 700 }}>
            {titulo} ({items.length})
          </h6>
          <div style={{ flex: 1 }}>
            {items.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>{vacioMsg}</p>
            ) : (
              items.map((it) => renderModuloCard(it, esDisponible))
            )}
          </div>
          {mostrarTotal && (
            <div
              style={{
                marginTop: '12px',
                paddingTop: '8px',
                borderTop: '1px solid #e5e7eb',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <span style={{ color: '#374151' }}>Horas:</span>
                <span style={{ color: '#475569' }}>{sumHoras(items)} h</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#374151' }}>Sumatorio:</span>
                <span style={{ color: '#059669' }}>{sumPrecio(items).toFixed(2)} €</span>
              </div>
            </div>
          )}
        </div>
      </Col>
    );
  };

  // Tarjeta simplificada para módulos de 2º año: clic añade/quita, sin bloques
  // Septiembre/Febrero. Las líneas incluidas permiten editar su precio (interno,
  // no suma al total); las disponibles solo muestran el precio por defecto.
  const renderModuloCardSegundo = (item, esDisponible) => (
    <div
      key={`${item.modulo}-${item.codigo}-${item.descripcion}`}
      role="button"
      tabIndex={0}
      onClick={() => (esDisponible ? addSegundoAnio(item) : removeSegundoAnio(item))}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (esDisponible) addSegundoAnio(item);
          else removeSegundoAnio(item);
        }
      }}
      style={{
        padding: '8px 10px',
        marginBottom: '6px',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontSize: '0.8rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', justifyContent: 'space-between' }}>
        <strong style={{ flex: 1 }}>
          {(item.nombreModulo || item.modulo || '').split(' - ')[0].trim()}
        </strong>
        <span style={chipStyle('#b45309')}>2º año</span>
      </div>
      {item.descripcion && (
        <div style={{ color: '#6b7280', fontSize: '0.72rem' }}>{item.descripcion}</div>
      )}
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
        {item.tramo && <span style={chipStyle(tramoBadgeColor(item.tramo))}>{item.tramo}</span>}
        {item.curso && <span style={chipStyle(cursoColor(item.curso))}>{cursoLabel(item.curso)}</span>}
        {item.horas != null && item.horas !== '' && (
          <span style={chipStyle('#475569')}>{item.horas}h</span>
        )}
        {esDisponible ? (
          item.precio != null &&
          item.precio !== '' && (
            <span style={chipStyle('#059669')}>{(parseFloat(item.precio) || 0).toFixed(2)} €</span>
          )
        ) : (
          <div
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <Input
              type="text"
              inputMode="decimal"
              bsSize="sm"
              placeholder="Precio"
              style={{ width: '90px', textAlign: 'right', fontSize: '0.72rem', padding: '2px 4px' }}
              value={item.precio ?? ''}
              onChange={(e) => handlePrecioSegundoChange(item, e.target.value)}
            />
            <span style={{ fontSize: '0.72rem', color: '#059669', fontWeight: 600 }}>€</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderColumnaSegundo = (titulo, items, vacioMsg, esDisponible) => (
    <Col md="6">
      <div
        style={{
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '12px',
          minHeight: '200px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <h6 style={{ marginBottom: '12px', fontSize: '0.85rem', fontWeight: 700 }}>
          {titulo} ({items.length})
        </h6>
        <div style={{ flex: 1 }}>
          {items.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>{vacioMsg}</p>
          ) : (
            items.map((it) => renderModuloCardSegundo(it, esDisponible))
          )}
        </div>
        {!esDisponible && items.length > 0 && (
          <div
            style={{
              marginTop: '12px',
              paddingTop: '8px',
              borderTop: '1px solid #e5e7eb',
              fontSize: '0.8rem',
              fontWeight: 600,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ color: '#374151' }}>Horas:</span>
            <span style={{ color: '#475569' }}>{sumHoras(items)} h</span>
          </div>
        )}
      </div>
    </Col>
  );

  const addPlan = () => {
    setPlanes((prev) => [
      ...prev,
      {
        Id: null,
        presu_id: parseInt(id, 10),
        Cuotas: 1,
        Fecha_ini: '',
        Fecha_fin: null,
        tipo: '',
        localKey: `tmp_${Date.now()}_${Math.random()}`,
      },
    ]);
  };

  const removePlan = (idx) => {
    setPlanes((prev) => prev.filter((_, i) => i !== idx));
  };

  const updatePlan = (idx, patch) => {
    setPlanes((prev) =>
      prev.map((p, i) => {
        if (i !== idx) return p;
        const next = { ...p, ...patch };
        if (patch.tipo === '1') {
          next.Fecha_fin = null;
        }
        if (
          patch.Fecha_ini !== undefined &&
          next.tipo !== '1' &&
          next.Fecha_fin &&
          patch.Fecha_ini &&
          next.Fecha_fin < patch.Fecha_ini
        ) {
          next.Fecha_fin = null;
        }
        return next;
      }),
    );
  };

  const planEquals = (a, b) =>
    a.Id === b.Id &&
    a.Cuotas === b.Cuotas &&
    a.Fecha_ini === b.Fecha_ini &&
    (a.Fecha_fin || null) === (b.Fecha_fin || null) &&
    (a.tipo || '') === (b.tipo || '');

  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  const handleGenerarPdf = async (mostrarPrecios) => {
    setPdfModalOpen(false);
    setGeneratingPdf(true);
    try {
      const blob = await presupuestosApiService.generarPresupuestoPDF({
        headquarter,
        id: parseInt(id, 10),
        mostrarPrecios,
      });
      const blobFinal = blob.slice ? blob.slice(0, blob.size, 'application/pdf') : blob;
      const url = URL.createObjectURL(blobFinal);
      window.open(url, '_blank');
    } catch (error) {
      logger.error('Error generando PDF del presupuesto:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleVerPdf = () => {
    if (puedeElegirPrecios) {
      setPdfModalOpen(true);
    } else {
      handleGenerarPdf(false);
    }
  };

  // PDF interno de secretaría (1er + 2º año). Visible para admins y OU secretaría
  // (ver puedeVerPdfInterno); el backend debe autorizar ambos o devolverá 403.
  const [generatingPdfInterno, setGeneratingPdfInterno] = useState(false);

  const handlePdfInterno = async () => {
    setGeneratingPdfInterno(true);
    try {
      const blob = await presupuestosApiService.generarPresupuestoPdfInterno({
        headquarter,
        id: parseInt(id, 10),
      });
      const blobFinal = blob.slice ? blob.slice(0, blob.size, 'application/pdf') : blob;
      const url = URL.createObjectURL(blobFinal);
      window.open(url, '_blank');
    } catch (error) {
      logger.error('Error generando PDF interno del presupuesto:', error);
    } finally {
      setGeneratingPdfInterno(false);
    }
  };

  const sincronizarPlanes = async () => {
    const presuId = parseInt(id, 10);

    // Borrar planes que ya no están en la lista actual
    const currentIds = planes.filter((p) => p.Id != null).map((p) => p.Id);
    const toDelete = planesOriginales.filter((p) => !currentIds.includes(p.Id));

    // Crear los nuevos (sin Id) con Fecha_ini válida
    const toCreate = planes.filter((p) => p.Id == null && p.Fecha_ini);

    // Actualizar los modificados
    const toUpdate = planes.filter((p) => {
      if (p.Id == null) return false;
      const original = planesOriginales.find((o) => o.Id === p.Id);
      return original && !planEquals(p, original);
    });

    const tasks = [];

    toDelete.forEach((p) => {
      tasks.push(presupuestosApiService.borrarPlanPago({ headquarter, Id: p.Id }));
    });

    toCreate.forEach((p) => {
      tasks.push(
        presupuestosApiService.crearPlanPago({
          headquarter,
          presu_id: presuId,
          Cuotas: p.Cuotas,
          Fecha_ini: p.Fecha_ini,
          Fecha_fin: p.tipo === '1' ? null : p.Fecha_fin || null,
          tipo: p.tipo || null,
        }),
      );
    });

    toUpdate.forEach((p) => {
      tasks.push(
        presupuestosApiService.actualizarPlanPago({
          headquarter,
          Id: p.Id,
          Cuotas: p.Cuotas,
          Fecha_ini: p.Fecha_ini,
          Fecha_fin: p.tipo === '1' ? null : p.Fecha_fin || null,
          tipo: p.tipo || null,
        }),
      );
    });

    if (tasks.length === 0) return;
    await Promise.all(tasks);
    await cargarPlanes();
  };

  const handleSave = async (sendEmail = false) => {
    setSaving(true);
    const estadoFinal = sendEmail ? 2 : estado;
    try {
      await presupuestosApiService.actualizarPresupuesto({
        headquarter,
        id: parseInt(id, 10),
        estado: estadoFinal,
        coment_int: comentInt,
        coment_ext: comentExt,
        Nombre: nombre,
        Apellidos: apellidos,
        DNI: dni,
        Correo: correo,
        // Solo se envía si tiene valor; vacío -> undefined (se omite del JSON) para
        // que el backend conserve el precio de inscripción actual.
        precio_inscr: precioInscr !== '' ? parseFloat(precioInscr) || 0 : undefined,
        actividades: [
          // 1er año: grupo de cabecera explícito en cada línea
          ...actividades.map((a) => ({
            ...a,
            seleccionada: true,
            precio: parseFloat(a.precio) || 0,
            idgrupo: grupoCabecera,
          })),
          // 2º año: líneas internas (no suman ni se envían al alumno). Ahora
          // comparten el grupo de cabecera, así que NO se pueden detectar por
          // idgrupo distinto: se envía el flag `interno: 1` para que el backend
          // las marque como tales. Completas porque el PUT no las enriquece.
          // Si secretaría ha fijado un precio, se envía y prevalece; si se deja
          // vacío, se omite para que el backend aplique el de curso_precio_sede.
          ...segundoAnio.map((a) => ({
            ...a,
            seleccionada: true,
            interno: 1,
            codigo: a.codigo ?? a.codigocurso,
            descripcion: a.descripcion ?? a.Descripcion,
            idAgrupado: a.idAgrupado ?? a.IdAgrupado ?? a.modulo,
            idPlan: a.idPlan ?? a.IdPlan,
            idaño: a.idaño ?? a.Idaño ?? a.IdAño,
            idgrupo: String(a.idgrupo),
            // Se envía solo si secretaría lo fijó; vacío -> undefined (se omite
            // del JSON) para que el backend aplique el precio por defecto.
            precio:
              a.precio != null && a.precio !== '' ? parseFloat(a.precio) || 0 : undefined,
          })),
        ],
        actividadesNo: disponibles.map((a) => ({ ...a, seleccionada: false })),
      });
      try {
        await sincronizarPlanes();
      } catch (planError) {
        logger.error('Error sincronizando planes de pago:', planError);
      }
      if (sendEmail) {
        try {
          await presupuestosApiService.enviarPresupuesto({
            headquarter,
            id: parseInt(id, 10),
          });
        } catch (mailError) {
          logger.error('Error enviando email del presupuesto:', mailError);
        }
      }
      if (estadoFinal !== estado) setEstado(estadoFinal);
      setSaved(true);
    } catch (error) {
      logger.error('Error actualizando presupuesto:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
      </div>
    );
  }

  if (!data) {
    return (
      <ComponentCard title="Presupuesto no encontrado" hasButton={false}>
        <p>No se ha podido cargar el presupuesto.</p>
        <Button onClick={() => navigate('/presupuestos')}><ArrowLeft size={14} /> Volver</Button>
      </ComponentCard>
    );
  }

  return (
    <div>
      {saved && <ModalClickable header="Presupuesto actualizado correctamente." />}
      <Modal isOpen={pdfModalOpen} toggle={() => setPdfModalOpen(false)} centered>
        <ModalHeader toggle={() => setPdfModalOpen(false)}>Generar PDF</ModalHeader>
        <ModalBody>¿Cómo quieres generar el PDF del presupuesto?</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPdfModalOpen(false)}>
            Cancelar
          </Button>
          <Button color="info" onClick={() => handleGenerarPdf(false)}>
            Sin precios
          </Button>
          <Button color="primary" onClick={() => handleGenerarPdf(true)}>
            Con precios
          </Button>
        </ModalFooter>
      </Modal>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', margin: 0 }}>
            Presupuesto #{data.Id}
          </h1>
          <p style={{ color: '#9ca3af', marginTop: '2px', marginBottom: 0, fontSize: '0.8rem' }}>
            Creado el {new Date(data.fecha).toLocaleDateString('es-ES')} por {data.idusuario}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            color="success"
            onClick={() => handleSave(false)}
            disabled={saving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
          >
            {saving ? <Spinner size="sm" /> : <Save size={14} />} Guardar cambios
          </Button>
          <Button
            color="info"
            onClick={handleVerPdf}
            disabled={generatingPdf}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
          >
            {generatingPdf ? <Spinner size="sm" /> : <FileText size={14} />} Ver PDF
          </Button>
          {puedeVerPdfInterno && (
            <Button
              color="warning"
              onClick={handlePdfInterno}
              disabled={generatingPdfInterno}
              title="PDF de secretaría con 1er y 2º año (no se envía al alumno)"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
            >
              {generatingPdfInterno ? <Spinner size="sm" /> : <FileText size={14} />} PDF interno
            </Button>
          )}
          <Button
            color="primary"
            onClick={() => handleSave(true)}
            disabled={saving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
          >
            {saving ? <Spinner size="sm" /> : <Send size={14} />} Guardar y enviar
          </Button>
        </div>
      </div>

      {/* Datos alumno */}
      <ComponentCard title="Datos del alumno" hasButton={false}>
        <Row>
          <Col md={2}>
            <Label><small className="text-muted">ID Alumno</small></Label>
            <Input value={data.CCODCLI} disabled />
          </Col>
          <Col md={3}>
            <Label><small>Nombre</small></Label>
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </Col>
          <Col md={3}>
            <Label><small>Apellidos</small></Label>
            <Input value={apellidos} onChange={(e) => setApellidos(e.target.value)} />
          </Col>
          <Col md={2}>
            <Label><small>DNI</small></Label>
            <Input value={dni} onChange={(e) => setDni(e.target.value)} />
          </Col>
          <Col md={2}>
            <Label><small>Correo</small></Label>
            <Input
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
            />
          </Col>
        </Row>
      </ComponentCard>

      {/* Datos editables */}
      <ComponentCard title="Datos del presupuesto" hasButton={false}>
        <Row>
          <Col md={4}>
            <Label><small>Estado</small></Label>
            <Input type="select" value={estado} onChange={(e) => setEstado(parseInt(e.target.value, 10))}>
              {ESTADOS.map((est) => (
                <option key={est.value} value={est.value}>{est.label}</option>
              ))}
            </Input>
            <Badge color={estadoColor[estado] || 'secondary'} style={{ fontSize: '0.7rem', marginTop: '4px' }}>
              {ESTADOS.find((e) => e.value === estado)?.label}
            </Badge>
          </Col>
          <Col md={2}>
            <Label><small>Grupo</small></Label>
            <Input value={data.Grupo || data.idgrupo || ''} disabled />
          </Col>
          <Col md={2}>
            <Label><small>Periodo</small></Label>
            <Input value={data.idperiodo || ''} disabled />
          </Col>
          <Col md={2}>
            <Label><small>Precio inscripción</small></Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Input
                type="text"
                inputMode="decimal"
                value={precioInscr}
                onChange={(e) => setPrecioInscr(limpiarPrecio(e.target.value))}
                style={{ textAlign: 'right' }}
                aria-label="Precio inscripción"
                title="Editable: se guarda al pulsar «Guardar cambios»"
              />
              <span style={{ fontWeight: 600, color: '#059669' }}>€</span>
            </div>
          </Col>
          <Col md={2}>
            <Label><small>Precio actividades</small></Label>
            <Input value={`${precioActividades.toFixed(2)} €`} disabled />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={6}>
            <Label><small>Comentario interno</small></Label>
            <MyEditor
              initialValue={comentInt}
              content={comentInt}
              setContent={setComentInt}
              initHeight={250}
            />
          </Col>
          <Col md={6}>
            <Label><small>Comentario externo</small></Label>
            <MyEditor
              initialValue={comentExt}
              content={comentExt}
              setContent={setComentExt}
              initHeight={250}
            />
          </Col>
        </Row>
      </ComponentCard>

      {/* Planes de pago */}
      <ComponentCard title="Planes de pago" hasButton={false}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                <th style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 600 }}>Cuotas</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', fontWeight: 600 }}>
                  Importe
                </th>
                <th style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 600 }}>
                  Fecha inicio
                </th>
                <th style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 600 }}>Tipo</th>
                <th style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 600 }}>
                  Fecha fin
                </th>
                <th style={{ width: '40px' }} />
              </tr>
            </thead>
            <tbody>
              {planes.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '12px 6px', color: '#9ca3af' }}>
                    No hay planes de pago. Pulsa &quot;Añadir plan&quot; para crear uno.
                  </td>
                </tr>
              )}
              {planes.map((plan, idx) => {
                const key = plan.Id != null ? `plan-${plan.Id}` : plan.localKey;
                const finDisabled = plan.tipo === '1';
                const nCuotas = parseInt(plan.Cuotas, 10) || 0;
                const importeCuota = nCuotas > 0 ? precioActividades / nCuotas : 0;
                return (
                  <tr key={key} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px' }}>
                      <Input
                        type="number"
                        bsSize="sm"
                        min="1"
                        max="12"
                        step="1"
                        value={plan.Cuotas}
                        onChange={(e) =>
                          updatePlan(idx, { Cuotas: parseInt(e.target.value, 10) || 1 })
                        }
                        style={{ width: '80px', fontSize: '0.8rem' }}
                      />
                    </td>
                    <td
                      style={{
                        padding: '6px',
                        textAlign: 'right',
                        color: '#059669',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {importeCuota.toFixed(2)} €{nCuotas > 1 ? ' / cuota' : ''}
                    </td>
                    <td style={{ padding: '6px' }}>
                      <Input
                        type="date"
                        bsSize="sm"
                        min={todayISO}
                        value={plan.Fecha_ini || ''}
                        onChange={(e) => updatePlan(idx, { Fecha_ini: e.target.value })}
                        style={{ fontSize: '0.8rem' }}
                      />
                    </td>
                    <td style={{ padding: '6px' }}>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={plan.tipo || ''}
                        onChange={(e) => updatePlan(idx, { tipo: e.target.value })}
                        style={{ fontSize: '0.8rem' }}
                      >
                        <option value="">-</option>
                        {TIPO_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                    </td>
                    <td style={{ padding: '6px' }}>
                      <Input
                        type="date"
                        bsSize="sm"
                        min={plan.Fecha_ini || todayISO}
                        value={finDisabled ? '' : plan.Fecha_fin || ''}
                        disabled={finDisabled}
                        onChange={(e) => updatePlan(idx, { Fecha_fin: e.target.value || null })}
                        style={{ fontSize: '0.8rem' }}
                      />
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      <Button
                        size="sm"
                        color="link"
                        onClick={() => removePlan(idx)}
                        style={{ color: '#dc2626', padding: '2px 6px' }}
                        title="Eliminar plan"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: '8px' }}>
          <Button
            size="sm"
            color="primary"
            outline
            onClick={addPlan}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            <Plus size={14} /> Añadir plan
          </Button>
        </div>
        <p style={{ marginTop: '8px', fontSize: '0.75rem', color: '#9ca3af' }}>
          Los cambios se guardan al pulsar &quot;Guardar cambios&quot;. Si el tipo es <strong>U</strong>{' '}
          la fecha fin queda en blanco automáticamente.
        </p>
      </ComponentCard>

      {/* Selección de módulos: 3 columnas (disponibles / Septiembre / Febrero) */}
      <ComponentCard title="Módulos del presupuesto" hasButton={false}>
        <Row>
          {renderColumna(
            'Módulos disponibles',
            disponibles,
            'Todos los módulos están asignados a un periodo.',
            false,
          )}
          {renderColumna(
            'Inicio Septiembre',
            septiembreItems,
            'Sin módulos en este periodo.',
            true,
            'septiembre',
          )}
          {renderColumna(
            'Inicio Febrero',
            febreroItems,
            'Sin módulos en este periodo.',
            true,
            'febrero',
          )}
        </Row>
      </ComponentCard>

      {/* 2º año interno: módulos del propio grupo del presupuesto, informativos */}
      <ComponentCard title="Módulos 2º año (interno)" hasButton={false}>
        <p style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '-8px' }}>
          Módulos del grupo del presupuesto que aún no están en la propuesta.
          Informativo para secretaría de cara a la renovación: estos módulos{' '}
          <strong>no suman al precio</strong> y <strong>no se envían al alumno</strong>. Solo
          aparecen en el PDF interno.
        </p>
        <Row>
          {renderColumnaSegundo(
            'Módulos disponibles (2º año)',
            disponiblesSegundo,
            loadingModulosSegundo
              ? 'Cargando módulos del grupo...'
              : 'No hay más módulos disponibles en este grupo.',
            true,
          )}
          {renderColumnaSegundo(
            'Incluidos en la propuesta (2º año)',
            segundoAnio,
            'Sin módulos de 2º año en la propuesta.',
            false,
          )}
        </Row>
      </ComponentCard>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
        <Button onClick={() => navigate('/presupuestos')}>
          <ArrowLeft size={14} /> Volver
        </Button>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            color="success"
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            {saving ? <Spinner size="sm" /> : <Save size={14} />} Guardar cambios
          </Button>
          <Button
            color="info"
            onClick={handleVerPdf}
            disabled={generatingPdf}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
          >
            {generatingPdf ? <Spinner size="sm" /> : <FileText size={14} />} Ver PDF
          </Button>
          {puedeVerPdfInterno && (
            <Button
              color="warning"
              onClick={handlePdfInterno}
              disabled={generatingPdfInterno}
              title="PDF de secretaría con 1er y 2º año (no se envía al alumno)"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
            >
              {generatingPdfInterno ? <Spinner size="sm" /> : <FileText size={14} />} PDF interno
            </Button>
          )}
          <Button
            style={{ backgroundColor: colors.primary, border: 'none' }}
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            {saving ? <Spinner size="sm" /> : <Send size={14} />} Guardar y enviar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DetallePresupuesto;
